import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

/**
 * @typedef {Object} ToastContextType
 * @property {Function} addToast - 添加Toast消息
 * @property {Function} removeToast - 移除Toast消息
 * @property {Function} success - 显示成功消息
 * @property {Function} error - 显示错误消息
 * @property {Function} info - 显示信息消息
 * @property {Function} warning - 显示警告消息
 */

const ToastContext = createContext(/** @type {ToastContextType|null} */ (null));

/**
 * 使用Toast的Hook
 * @returns {ToastContextType}
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

/**
 * Toast Provider组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    
    setToasts(prev => [...prev, newToast]);
    
    // 自动移除
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
    
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // 便捷方法
  const success = useCallback((message, duration) => {
    return addToast(message, 'success', duration);
  }, [addToast]);

  const error = useCallback((message, duration) => {
    return addToast(message, 'error', duration);
  }, [addToast]);

  const info = useCallback((message, duration) => {
    return addToast(message, 'info', duration);
  }, [addToast]);

  const warning = useCallback((message, duration) => {
    return addToast(message, 'warning', duration);
  }, [addToast]);

  const value = {
    addToast,
    removeToast,
    success,
    error,
    info,
    warning
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * Toast容器组件
 * @param {Object} props
 * @param {Array} props.toasts - Toast消息数组
 * @param {Function} props.onRemove - 移除回调
 */
const ToastContainer = ({ toasts, onRemove }) => {
  return (
    <div 
      className="fixed top-4 right-4 z-[9999] space-y-2"
      role="region"
      aria-label="通知消息"
    >
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

/**
 * 单个Toast组件
 * @param {Object} props
 * @param {Object} props.toast - Toast数据
 * @param {Function} props.onRemove - 移除回调
 */
const ToastItem = ({ toast, onRemove }) => {
  const { id, message, type } = toast;

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500" aria-hidden="true" />,
    error: <AlertCircle className="w-5 h-5 text-red-500" aria-hidden="true" />,
    warning: <AlertCircle className="w-5 h-5 text-yellow-500" aria-hidden="true" />,
    info: <Info className="w-5 h-5 text-blue-500" aria-hidden="true" />
  };

  const styles = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  };

  const ariaLabels = {
    success: '成功',
    error: '错误',
    warning: '警告',
    info: '信息'
  };

  return (
    <div 
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg min-w-[300px] max-w-[500px] animate-slide-in ${styles[type]}`}
      role="alert"
      aria-live="polite"
      aria-label={ariaLabels[type]}
    >
      {icons[type]}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button 
        onClick={() => onRemove(id)}
        className="p-1 hover:bg-black/5 rounded-full transition-colors"
        aria-label="关闭通知"
        type="button"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default ToastContext;
